import React, { useState } from 'react';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Plus,
  BookOpen,
  CheckCircle2,
  Edit3,
} from 'lucide-react';
import type { PlanoAula, DiaSemana, CreatePlanoAulaInput, StatusPlanoAula } from '../../api/hooks/usePlanoAula';
import { getSemanaAno } from '../../api/hooks/usePlanoAula';

// ── Constantes ───────────────────────────────────────────────────────────

const DIAS: { key: DiaSemana; label: string; abrev: string }[] = [
  { key: 'SEGUNDA', label: 'Segunda', abrev: 'SEG' },
  { key: 'TERCA', label: 'Terça', abrev: 'TER' },
  { key: 'QUARTA', label: 'Quarta', abrev: 'QUA' },
  { key: 'QUINTA', label: 'Quinta', abrev: 'QUI' },
  { key: 'SEXTA', label: 'Sexta', abrev: 'SEX' },
];

const STATUS_COLORS: Record<StatusPlanoAula, { bg: string; text: string; label: string }> = {
  RASCUNHO: { bg: 'rgba(107,114,128,0.1)', text: '#6b7280', label: 'Rascunho' },
  PLANEJADO: { bg: 'rgba(59,130,246,0.1)', text: '#3b82f6', label: 'Planejado' },
  EXECUTADO: { bg: 'rgba(34,197,94,0.1)', text: '#22c55e', label: 'Executado' },
  CANCELADO: { bg: 'rgba(239,68,68,0.1)', text: '#ef4444', label: 'Cancelado' },
};

// ── Props ────────────────────────────────────────────────────────────────

interface PlanoAulaSemanalProps {
  planos: PlanoAula[];
  professorId: string;
  semanaAno: number;
  anoLetivo: number;
  /** Coluna lateral do dashboard desktop — layout vertical compacto */
  variant?: 'default' | 'sidebar';
  isLoading?: boolean;
  onNavSemana: (delta: number) => void;
  onCriar?: (input: CreatePlanoAulaInput) => void;
  onEditar?: (plano: PlanoAula) => void;
  onMudarStatus?: (id: string, status: StatusPlanoAula) => void;
  turmas?: { id: string; nome: string }[];
  materias?: { id: string; nome: string }[];
}

/**
 * Componente de visualização semanal dos planos de aula.
 * Exibe os dias da semana como colunas com cards de plano.
 */
export const PlanoAulaSemanal: React.FC<PlanoAulaSemanalProps> = ({
  planos,
  semanaAno,
  anoLetivo,
  variant = 'default',
  isLoading,
  onNavSemana,
  onEditar,
  onMudarStatus,
}) => {
  const isSidebar = variant === 'sidebar';
  const [diaSelecionado, setDiaSelecionado] = useState<DiaSemana | null>(null);

  // Agrupar planos por dia da semana
  const planosPorDia = DIAS.reduce(
    (acc, dia) => {
      acc[dia.key] = planos.filter((p) => p.diaSemana === dia.key);
      return acc;
    },
    {} as Record<DiaSemana, PlanoAula[]>,
  );

  // Calcular semana atual para destacar
  const semanaAtual = getSemanaAno(new Date());
  const anoAtual = new Date().getFullYear();
  const isSemanaCurrent = semanaAno === semanaAtual && anoLetivo === anoAtual;

  return (
    <div style={isSidebar ? { ...styles.container, ...styles.containerSidebar } : styles.container}>
      {/* Header com navegação de semana */}
      <div style={isSidebar ? { ...styles.header, ...styles.headerSidebar } : styles.header}>
        <div style={styles.headerLeft}>
          <Calendar size={isSidebar ? 16 : 20} style={{ color: '#8b5cf6' }} />
          <h3 style={isSidebar ? { ...styles.title, ...styles.titleSidebar } : styles.title}>
            Plano Semanal
          </h3>
          {isSemanaCurrent && (
            <span style={styles.badgeCurrent}>Semana Atual</span>
          )}
        </div>
        <div style={isSidebar ? { ...styles.navButtons, width: '100%', justifyContent: 'space-between' } : styles.navButtons}>
          <button onClick={() => onNavSemana(-1)} style={isSidebar ? { ...styles.navBtn, ...styles.navBtnSidebar } : styles.navBtn} title="Semana anterior">
            <ChevronLeft size={isSidebar ? 14 : 18} />
          </button>
          <span style={isSidebar ? { ...styles.weekLabel, ...styles.weekLabelSidebar } : styles.weekLabel}>
            Sem. {semanaAno} · {anoLetivo}
          </span>
          <button onClick={() => onNavSemana(1)} style={isSidebar ? { ...styles.navBtn, ...styles.navBtnSidebar } : styles.navBtn} title="Próxima semana">
            <ChevronRight size={isSidebar ? 14 : 18} />
          </button>
        </div>
      </div>

      {/* Grid de dias - ou CTA compacto se vazio */}
      {isLoading ? (
        <div
          style={
            isSidebar
              ? { ...styles.loading, padding: 16, fontSize: '0.75rem', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }
              : styles.loading
          }
        >
          Carregando planos...
        </div>
      ) : planos.length === 0 ? (
        <div
          style={
            isSidebar
              ? styles.emptySidebar
              : {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 12,
                  padding: '20px 24px',
                  color: 'var(--text-muted, #999)',
                  fontSize: '0.85rem',
                }
          }
        >
          <Calendar size={isSidebar ? 16 : 18} style={{ opacity: 0.5, flexShrink: 0 }} />
          <span>Nenhum plano esta semana.</span>
          <button
            onClick={() => {
              const el = document.getElementById('plano-semanal-section');
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }}
            style={
              isSidebar
                ? styles.planejarBtnSidebar
                : {
                    border: '1px solid #8b5cf6',
                    background: 'rgba(139,92,246,0.08)',
                    color: '#8b5cf6',
                    borderRadius: 8,
                    padding: '6px 14px',
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }
            }
          >
            Planejar
          </button>
        </div>
      ) : (
        <div style={isSidebar ? styles.gridSidebar : styles.grid}>
          {DIAS.map((dia) => {
            const dayPlanos = planosPorDia[dia.key];
            const isSelected = diaSelecionado === dia.key;

            return (
              <div
                key={dia.key}
                style={{
                  ...styles.dayColumn,
                  ...(isSidebar ? styles.dayColumnSidebar : {}),
                  ...(isSelected ? styles.dayColumnActive : {}),
                }}
                onClick={() => setDiaSelecionado(isSelected ? null : dia.key)}
              >
                <div style={styles.dayHeader}>
                  <span style={styles.dayAbrev}>{dia.abrev}</span>
                  <span style={styles.dayCount}>
                    {dayPlanos.length > 0 ? `${dayPlanos.length} aula${dayPlanos.length > 1 ? 's' : ''}` : '—'}
                  </span>
                </div>

                {dayPlanos.length === 0 ? (
                  <div style={styles.empty}>
                    <Plus size={16} style={{ color: '#ccc' }} />
                  </div>
                ) : (
                  dayPlanos.map((plano) => {
                    const statusInfo = STATUS_COLORS[plano.status];
                    return (
                      <div key={plano.id} style={styles.card}>
                        <div style={styles.cardHeader}>
                          <span
                            style={{
                              ...styles.statusBadge,
                              background: statusInfo.bg,
                              color: statusInfo.text,
                            }}
                          >
                            {statusInfo.label}
                          </span>
                          <div style={styles.cardActions}>
                            {onEditar && (
                              <button
                                style={styles.iconBtn}
                                onClick={(e) => { e.stopPropagation(); onEditar(plano); }}
                                title="Editar"
                              >
                                <Edit3 size={13} />
                              </button>
                            )}
                            {onMudarStatus && plano.status === 'PLANEJADO' && (
                              <button
                                style={styles.iconBtn}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onMudarStatus(plano.id, 'EXECUTADO');
                                }}
                                title="Marcar como executado"
                              >
                                <CheckCircle2 size={13} style={{ color: '#22c55e' }} />
                              </button>
                            )}
                          </div>
                        </div>

                        <p style={styles.tema}>{plano.tema}</p>

                        <div style={styles.cardMeta}>
                          <span style={styles.metaItem}>
                            <BookOpen size={11} />
                            {plano.materia?.nome ?? 'Matéria'}
                          </span>
                          <span style={styles.metaItem}>
                            {plano.turma?.nome ?? 'Turma'}
                          </span>
                        </div>

                        {plano.objetivos && (
                          <p style={styles.objetivos}>
                            {plano.objetivos.slice(0, 80)}{plano.objetivos.length > 80 ? '...' : ''}
                          </p>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Styles ─────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  container: {
    background: 'var(--bg-card, #fff)',
    borderRadius: 16,
    border: '1px solid var(--border-color, #e5e7eb)',
    overflow: 'hidden',
  },
  containerSidebar: {
    borderRadius: 10,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: '1px solid var(--border-color, #e5e7eb)',
    flexWrap: 'wrap' as const,
    gap: 10,
  },
  headerSidebar: {
    flexDirection: 'column',
    alignItems: 'stretch',
    padding: '10px 12px',
    gap: 8,
  },
  titleSidebar: {
    fontSize: '0.8rem',
  },
  weekLabelSidebar: {
    fontSize: '0.68rem',
    minWidth: 0,
    flex: 1,
  },
  navBtnSidebar: {
    width: 28,
    height: 28,
  },
  emptySidebar: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '14px 12px',
    color: 'var(--text-muted, #999)',
    fontSize: '0.72rem',
    textAlign: 'center',
    flex: 1,
    minHeight: 0,
  },
  planejarBtnSidebar: {
    border: '1px solid #8b5cf6',
    background: 'rgba(139,92,246,0.08)',
    color: '#8b5cf6',
    borderRadius: 6,
    padding: '5px 12px',
    fontSize: '0.68rem',
    fontWeight: 600,
    cursor: 'pointer',
    width: '100%',
  },
  gridSidebar: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
  },
  dayColumnSidebar: {
    minHeight: 'auto',
    borderRight: 'none',
    borderBottom: '1px solid var(--border-color, #f0f0f0)',
    padding: '8px 10px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  title: {
    margin: 0,
    fontSize: '1rem',
    fontWeight: 700,
    color: 'var(--text-main, #111)',
  },
  badgeCurrent: {
    padding: '2px 8px',
    borderRadius: 20,
    background: 'rgba(139,92,246,0.12)',
    color: '#8b5cf6',
    fontSize: '0.7rem',
    fontWeight: 600,
  },
  navButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  navBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    border: '1px solid var(--border-color, #d1d5db)',
    background: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: 'var(--text-main, #333)',
    transition: 'background 0.15s',
  },
  weekLabel: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: 'var(--text-main, #333)',
    minWidth: 130,
    textAlign: 'center' as const,
  },
  loading: {
    padding: 40,
    textAlign: 'center' as const,
    color: 'var(--text-muted, #999)',
    fontSize: '0.9rem',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: 0,
  },
  dayColumn: {
    minHeight: 200,
    borderRight: '1px solid var(--border-color, #f0f0f0)',
    padding: 10,
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  dayColumnActive: {
    background: 'rgba(139,92,246,0.04)',
  },
  dayHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
    paddingBottom: 8,
    borderBottom: '2px solid var(--border-color, #e5e7eb)',
  },
  dayAbrev: {
    fontSize: '0.78rem',
    fontWeight: 700,
    color: 'var(--text-main, #333)',
    letterSpacing: 0.5,
  },
  dayCount: {
    fontSize: '0.7rem',
    color: 'var(--text-muted, #999)',
  },
  empty: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 60,
    borderRadius: 8,
    border: '1px dashed var(--border-color, #ddd)',
    opacity: 0.5,
  },
  card: {
    padding: 10,
    borderRadius: 10,
    background: 'var(--bg-main, #f9fafb)',
    border: '1px solid var(--border-color, #e5e7eb)',
    marginBottom: 8,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  statusBadge: {
    padding: '2px 7px',
    borderRadius: 6,
    fontSize: '0.65rem',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
  },
  cardActions: {
    display: 'flex',
    gap: 4,
  },
  iconBtn: {
    width: 24,
    height: 24,
    borderRadius: 6,
    border: 'none',
    background: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: 'var(--text-muted, #999)',
    transition: 'color 0.15s',
  },
  tema: {
    margin: '0 0 6px',
    fontSize: '0.82rem',
    fontWeight: 600,
    color: 'var(--text-main, #111)',
    lineHeight: 1.3,
  },
  cardMeta: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap' as const,
    marginBottom: 4,
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 3,
    fontSize: '0.7rem',
    color: 'var(--text-muted, #888)',
  },
  objetivos: {
    margin: '6px 0 0',
    fontSize: '0.72rem',
    color: 'var(--text-muted, #777)',
    lineHeight: 1.4,
    fontStyle: 'italic' as const,
  },
};
